// src/context/AuthContext.js
import React, { createContext, useEffect, useState, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import config from '../config';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loggedUser, setUser] = useState(null);
  const [clientStatus, setClientStatus] = useState('active'); // Track client status
  const [loading, setLoading] = useState(true); // NEW

  const fetchUser = useCallback(async (token) => {
    if (loggedUser) return;
    try {
      const decoded = jwtDecode(token);
      const res = await axios.get(`${config.API_BASE_URL}/staff/${decoded.userId}/permissions`);
      setUser(res.data);
      setIsAuthenticated(true);

      // Store user info in localStorage for API logging
      localStorage.setItem('loggedUser', JSON.stringify(res.data));

      // Check client status after fetching user
      await checkClientStatus();
    } catch (err) {
      console.error('User fetch failed', err);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false); // Always end loading
    }
  },[loggedUser]);

  const checkClientStatus = async () => {
    try {
      const hostname = window.location.hostname;
      const subdomain = hostname.includes('localhost')
        ? hostname 
        : hostname;
        // : hostname.split('.')[0];

      // console.log('Checking client status for subdomain:', subdomain);

      const response = await axios.get(`${config.API_BASE_URL}/master/clients/${subdomain}`);

      const status = response.data.status || 'active';
      // console.log('Client status:', status);
      setClientStatus(status);
    } catch (error) {
      console.error('Failed to check client status:', error);
      setClientStatus('active');
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !isTokenExpired(token)) {
      fetchUser(token);
    } else {
      setIsAuthenticated(false);
      setUser(null);
      setLoading(false); // End loading
    }

    const handleStorage = () => {
      const token = localStorage.getItem('token');
      if (token && !isTokenExpired(token)) {
        fetchUser(token);
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setClientStatus('active'); // Reset status on logout
        setLoading(false); // End loading
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [fetchUser]);

  const login = async (token) => {
    localStorage.setItem('token', token);
    await fetchUser(token);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsAuthenticated(false);
    setClientStatus('active');
  };

  return (
    <AuthContext.Provider value={{
      loggedUser,
      isAuthenticated,
      clientStatus,
      login,
      logout,
      loading,
      checkClientStatus
    }}>
      {children}
    </AuthContext.Provider>
  );
};

function isTokenExpired(token) {
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1]));
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
}
